"use client";

import { useMemo } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphRealizationsTestimonials as ParagraphRealizationsTestimonialsData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphRealizationsTestimonials.module.css";

/**
 * Layout 65 - `PARAGRAPH_REALIZATIONS_TESTIMONIALS_LAYOUT`.
 *
 * Struktura:
 *   - Header centrowany: eyebrow (text_1) + H2 (name)
 *   - Grid 3-kartowy realizacji (obrazek/logo + label pill + tytul + short_text + CTA)
 *   - Separator
 *   - Testimonial carousel (Swiper single-slide) z dużym cytatem + autorem + dots
 *
 * Style w local module.css - port 1:1 z WRealizationsTestimonials.module.css.
 */
export function ParagraphRealizationsTestimonials({
  paragraph,
  t,
}: {
  paragraph: ParagraphRealizationsTestimonialsData;
  t: ParagraphTranslations;
}) {
  const realizations = useMemo(() => paragraph.realizations ?? [], [paragraph]);
  const testimonials = useMemo(() => paragraph.testimonials ?? [], [paragraph]);

  const eyebrow = (paragraph.hero_text ?? "").trim();
  const heading = paragraph.title;
  const hasRealizations = realizations.length > 0;
  const hasTestimonials = testimonials.length > 0;

  if (!hasRealizations && !hasTestimonials) return null;

  const tr = t.realizationsTestimonials;
  const seeAllHref = paragraph.href ? extractInternalHref(paragraph.href) ?? paragraph.href : null;
  const seeAllLabel = paragraph.see_all_label ?? tr.seeAll;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
      </div>

      {hasRealizations ? (
        <div className={styles.grid}>
          {realizations.map((item) => {
            const href = extractInternalHref(item.href);
            return (
              <Link key={item.id} href={href ?? item.href} className={styles.card}>
                <div className={styles.cardMedia}>
                  {item.picture ? (
                    <img src={item.picture} alt={item.name} loading="lazy" className={styles.cardImg} />
                  ) : (
                    <span className={styles.cardImgPlaceholder} aria-hidden="true" />
                  )}
                  {item.label ? <span className={styles.cardLabel}>{item.label}</span> : null}
                  {item.logo ? (
                    <span className={styles.cardLogo}>
                      <img src={item.logo} alt="" loading="lazy" />
                    </span>
                  ) : null}
                </div>
                <div className={styles.cardBody}>
                  <h3 className={styles.cardTitle}>{item.name}</h3>
                  {item.short_text ? <p className={styles.cardSector}>{item.short_text}</p> : null}
                  <span className={styles.cardCta}>{tr.seeRealization}</span>
                </div>
              </Link>
            );
          })}
        </div>
      ) : null}

      {seeAllHref ? (
        <div className={styles.allWrap}>
          <Link href={seeAllHref} className={styles.allBtn}>{seeAllLabel}</Link>
        </div>
      ) : null}

      {hasTestimonials ? (
        <div className={styles.testimonialsBlock}>
          <Swiper
            modules={[Autoplay, Pagination, A11y]}
            slidesPerView={1}
            loop={testimonials.length > 1}
            autoplay={testimonials.length > 1 ? { delay: 7000, disableOnInteraction: false } : false}
            pagination={testimonials.length > 1 ? { clickable: true, el: `.${styles.pagination}` } : false}
            a11y={{ enabled: true, prevSlideMessage: tr.prev, nextSlideMessage: tr.next, paginationBulletMessage: tr.goToSlide }}
            spaceBetween={0}
            className={styles.swiper}
          >
            {testimonials.map((item) => (
              <SwiperSlide key={item.id} className={styles.slide}>
                <div
                  className={styles.testimonial}
                  dangerouslySetInnerHTML={{ __html: item.testimonial_html }}
                />
                <p className={styles.author}>
                  <span className={styles.authorSep} aria-hidden="true" />
                  <span className={styles.authorName}>
                    {item.client_name}
                    {item.client_sector ? "," : null}
                  </span>
                  {item.client_sector ? (
                    <span className={styles.authorSector}>{item.client_sector}</span>
                  ) : null}
                  <span className={styles.authorSep} aria-hidden="true" />
                </p>
              </SwiperSlide>
            ))}
          </Swiper>
          {testimonials.length > 1 ? <div className={styles.pagination} /> : null}
        </div>
      ) : null}
    </section>
  );
}
